import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import AppInputNew from "@/components/Core/AppInput/AppInputNew";
import Button from "@/components/Core/Button/Button";
import Checkbox from "@/components/Core/Checkbox/Checkbox";
import KnowledgeBaseTrigger from "@/components/Shared/KnowledgeBase/KnowledgeBaseTrigger";
import { DenialReason } from "@/models/Organisation";
import { useOrganisation, useUpdateOrganisation } from "@/networking/organisation.hooks";
import { SettingsLeaf } from "./SettingsLeafLayout";

/**
 * "Neka uppdrag" — configure the reasons offered when an admin denies a job.
 *
 * The feature is gated behind a master toggle. There's no dedicated `enabled`
 * flag on `denialReasonSettings`, and the deny-time picker (JobDetailsPage)
 * already treats "no reasons configured" as off — so the toggle is derived:
 * on when anything is configured, and turning it off persists inert settings.
 */
const NekningsanledningarPage = () => {
	const { t } = useTranslation();
	const { data: organisation } = useOrganisation();
	const { mutate: updateOrganisation, isLoading: isSavingOrganisation } = useUpdateOrganisation();

	const [enabled, setEnabled] = useState(false);
	const [denialRequiredDraft, setDenialRequiredDraft] = useState(false);
	const [denialAllowOtherDraft, setDenialAllowOtherDraft] = useState(false);
	const [denialReasonsDraft, setDenialReasonsDraft] = useState<DenialReason[]>([]);

	useEffect(() => {
		const settings = organisation?.denialReasonSettings;
		const required = settings?.required ?? false;
		const allowOther = settings?.allowOther ?? false;
		const reasons = settings?.reasons ?? [];
		setDenialRequiredDraft(required);
		setDenialAllowOtherDraft(allowOther);
		setDenialReasonsDraft(reasons);
		setEnabled(reasons.length > 0 || required || allowOther);
	}, [organisation?.denialReasonSettings]);

	const newDenialReason = (): DenialReason => ({
		key: `reason_${Math.random().toString(36).slice(2, 10)}`,
		name: { sv: "" },
	});

	const toggleEnabled = (checked: boolean) => {
		setEnabled(checked);
		// Seed a few empty rows so the user has somewhere to type when enabling
		// from scratch. Empty rows are filtered out again on save.
		if (checked && denialReasonsDraft.length === 0) {
			setDenialReasonsDraft(Array.from({ length: 4 }, newDenialReason));
		}
	};

	const addDenialReason = () => setDenialReasonsDraft([...denialReasonsDraft, newDenialReason()]);

	const updateDenialReasonName = (index: number, sv: string) =>
		setDenialReasonsDraft(
			denialReasonsDraft.map((r, i) => (i === index ? { ...r, name: { sv } } : r))
		);

	const removeDenialReason = (index: number) =>
		setDenialReasonsDraft(denialReasonsDraft.filter((_, i) => i !== index));

	// What we'd persist for the current UI state. When the feature is off the
	// settings are saved inert so the deny-time picker stays hidden. "OTHER" is
	// reserved for the free-text fallback and must not be a configured reason.
	const buildPayload = () =>
		enabled
			? {
					required: denialRequiredDraft,
					allowOther: denialAllowOtherDraft,
					reasons: denialReasonsDraft
						.filter((r) => r.name.sv.trim().length > 0 && r.key !== "OTHER")
						.map((r) => ({ key: r.key, name: { sv: r.name.sv.trim() } })),
			  }
			: { required: false, allowOther: false, reasons: [] };

	const original = {
		required: organisation?.denialReasonSettings?.required ?? false,
		allowOther: organisation?.denialReasonSettings?.allowOther ?? false,
		reasons: organisation?.denialReasonSettings?.reasons ?? [],
	};

	const hasUnsavedChanges = JSON.stringify(buildPayload()) !== JSON.stringify(original);

	const saveDenialReasons = () => updateOrganisation({ denialReasonSettings: buildPayload() });

	return (
		<SettingsLeaf
			title={
				<span className="inline-flex items-center gap-1.5">
					Neka uppdrag
					<KnowledgeBaseTrigger tag="denial-reasons" />
				</span>
			}
			description="Aktivera för att låta administratörer ange en anledning när ett uppdrag nekas."
		>
			<Checkbox
				label="Aktivera anledningar vid nekande av uppdrag"
				isChecked={enabled}
				onChange={(e: any) => toggleEnabled(e.target.checked)}
			/>

			{enabled && (
				<>
					<div className="space-y-2">
						<Checkbox
							label={t("DENIAL_REASON_REQUIRED")}
							isChecked={denialRequiredDraft}
							onChange={(e: any) => setDenialRequiredDraft(e.target.checked)}
						/>
						<Checkbox
							label={t("DENIAL_REASON_ALLOW_OTHER")}
							isChecked={denialAllowOtherDraft}
							onChange={(e: any) => setDenialAllowOtherDraft(e.target.checked)}
						/>
					</div>

					<div className="flex max-w-[520px] flex-col gap-2">
						{denialReasonsDraft.map((reason, index) => (
							<div key={reason.key} className="flex items-start gap-2">
								<AppInputNew
									containerClassName="flex-1 min-w-0"
									value={reason.name.sv}
									onChangeText={(text) => updateDenialReasonName(index, text)}
									placeholder="DENIAL_REASON_NAME_PLACEHOLDER"
								/>
								<button
									type="button"
									onClick={() => removeDenialReason(index)}
									aria-label={t("REMOVE")}
									title={t("REMOVE")}
									className="flex h-[2.3125rem] w-[2.3125rem] shrink-0 items-center justify-center rounded-sm border border-transparent text-gray-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
								>
									<Trash2 size={18} />
								</button>
							</div>
						))}
					</div>

					<div className="max-w-[520px]">
						<button
							type="button"
							onClick={addDenialReason}
							className="flex items-center gap-1.5 rounded-sm px-1 py-1 text-sm font-medium text-action transition-colors hover:bg-action/10"
						>
							<Plus size={16} />
							{t("DENIAL_REASON_ADD")}
						</button>
					</div>
				</>
			)}

			<div>
				<Button
					onClick={saveDenialReasons}
					disabled={!hasUnsavedChanges || isSavingOrganisation}
					height="37px"
					fontSize="s"
				>
					{t("SAVE_CHANGES")}
				</Button>
			</div>
		</SettingsLeaf>
	);
};

export default NekningsanledningarPage;
